import type { BookDto } from './book';

export const BookStatus = {
  Draft: 1,
  Published: 2,
  Archived: 3,
} as const;
export type BookStatus = typeof BookStatus[keyof typeof BookStatus];

export const BookFormat = {
  EPUB: 1,
  PDF: 2,
} as const;
export type BookFormat = typeof BookFormat[keyof typeof BookFormat];

/**
 * Concrete renderer a Book uses. Every Book has exactly one value.
 * CustomHtml / EpubJs render EPUB content; Pdf is the value all PDF books carry
 * (they render through UnifiedPdfRenderer, not either EPUB renderer). Name kept
 * as EpubRendererType for backward compatibility; a future rename to
 * BookRendererType would be source-only.
 */
export const EpubRendererType = {
  CustomHtml: 1,
  EpubJs: 2,
  Pdf: 3,
} as const;
export type EpubRendererType = typeof EpubRendererType[keyof typeof EpubRendererType];

export interface BookFilterState {
  q?: string;
  status?: BookStatus;
  format?: BookFormat;
  publisherId?: string;
  drm?: boolean;
  rendererType?: EpubRendererType;
  complexityMin?: number;
  complexityMax?: number;
  createdAfter?: string;    // ISO date
  createdBefore?: string;   // ISO date
  hasCover?: boolean;
  includeDeleted?: boolean;
  /**
   * When true, show archived books (successors of past renderer switches)
   * alongside the active catalog. Distinct from `includeDeleted` which
   * targets ABP's soft-delete flag.
   */
  includeArchived?: boolean;
  sort?: string;            // e.g. 'creationTime', 'title'
  dir?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const DEFAULT_FILTER_STATE: BookFilterState = {
  page: 1,
  pageSize: 25,
  sort: 'creationTime',
  dir: 'desc',
};

export interface BookSavedView {
  id: string;
  name: string;
  createdAt: string;
  filters: BookFilterState;
}

export interface SavedViewsStore {
  version: 1;
  views: BookSavedView[];
}

export interface UpdateBookManageDto {
  title: string;
  author: string;
  isbn?: string;
  publisherId: string;
  status: BookStatus;
  subjects?: string;
  contributors?: string;
  rights?: string;
  complexityScore?: number;
  isDrmEnabled: boolean;
  coverImageUrl?: string;
  concurrencyStamp: string;
}

export interface BulkStatusUpdateDto {
  bookIds: string[];
  status: BookStatus;
}

export interface BulkQuotaUpdateDto {
  bookIds: string[];
  isDrmEnabled?: boolean;
  defaultPrintQuotaLimit?: number;
  defaultPrintQuotaWindowDays?: number;
  defaultCopyQuotaLimit?: number;
  defaultCopyQuotaWindowDays?: number;
  defaultDownloadQuotaLimit?: number;
  defaultDownloadQuotaWindowDays?: number;
}

export interface BulkItemResult {
  bookId: string;
  errorMessage: string;
}

export interface BulkOperationResultDto {
  successCount: number;
  failureCount: number;
  failures: BulkItemResult[];
}

export interface ManageBooksListResult {
  items: BookDto[];
  totalCount: number;
}

/**
 * Resolve a raw book cover path (as stored on BookDto.coverImageUrl) into a
 * browser-displayable URL.
 *
 * Books uploaded via the existing flow store the raw blob path
 * (e.g. `books/<id>/cover.jpg`). The backend exposes `/api/app/books-extn/image?path=...`
 * which streams the blob. Older records may already hold a wrapped URL — pass
 * those through unchanged.
 */
export function resolveCoverUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (trimmed.startsWith('/api/') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `/api/app/books-extn/image?path=${encodeURIComponent(trimmed)}`;
}

/**
 * Background-job types produced by the switch-renderer and related pipelines.
 * Integer values must match backend enum learnleaf.Books.BookProcessingJobType.
 */
export const BookProcessingJobType = {
  Upload: 1,
  SearchIndex: 2,
  CfiLocations: 3,
} as const;
export type BookProcessingJobType = typeof BookProcessingJobType[keyof typeof BookProcessingJobType];

/**
 * Status lifecycle for a BookProcessingStatus row. Integer values must match
 * backend enum learnleaf.Books.BookProcessingJobStatus.
 */
export const BookProcessingJobStatus = {
  Pending: 1,
  Running: 2,
  Completed: 3,
  Failed: 4,
  Cancelled: 5,
} as const;
export type BookProcessingJobStatus = typeof BookProcessingJobStatus[keyof typeof BookProcessingJobStatus];

export interface BookProcessingStatusDto {
  id: string;
  bookId: string;
  jobType: BookProcessingJobType;
  status: BookProcessingJobStatus;
  hangfireJobId?: string | null;
  attemptCount: number;
  lastError?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  creationTime: string;
  lastModificationTime?: string | null;
}
